'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import LuxuryModal from '@/components/ui/LuxuryModal';
import { useChat } from '@/lib/hooks/useChat';
import toast from 'react-hot-toast';

// ─── Types ────────────────────────────────────────────────────────────────────
interface PackageComponent {
  id: string;
  type: 'flight' | 'hotel' | 'activity' | 'tour' | 'cruise' | 'transfer' | 'bus' | 'insurance';
  supplier: string;
  product_name: string;
  description: string;
  estimated_price_usd: number;
  duration: string;
  day: number;
  recommended?: boolean;
  reason?: string;
}

interface PricingSummary {
  subtotal: number;
  discount_applied: number;
  discount_type: string;
  discount_amount: number;
  after_discount: number;
  taxes: number;
  service_fee: number;
  grand_total_usd: number;
  grand_total_display: number;
  display_currency: string;
  total_commission: number;
  estimated_profit: number;
  profit_margin: number;
}

interface OptimizationResult {
  optimized_components: PackageComponent[];
  total_estimated_cost: number;
  savings_vs_retail: number;
  day_by_day_itinerary: Array<{ day: number; title: string; activities: string[]; accommodation: string }>;
  upgrades_suggested: Array<{ component: string; upgrade: string; additional_cost: number; benefit: string }>;
  alternatives: Array<{ component: string; alternative: string; price_difference: number }>;
  cross_sell: Array<{ service: string; supplier: string; price: number; reason: string }>;
  optimization_summary: string;
  package_highlights: string[];
  ai_confidence_score: number;
}

const COMPONENT_TYPES = [
  { id: 'flight', label: 'Flights', icon: 'PaperAirplaneIcon', color: 'text-blue-400' },
  { id: 'hotel', label: 'Hotels', icon: 'BuildingOffice2Icon', color: 'text-amber-400' },
  { id: 'activity', label: 'Activities', icon: 'StarIcon', color: 'text-green-400' },
  { id: 'tour', label: 'Tours', icon: 'MapIcon', color: 'text-purple-400' },
  { id: 'cruise', label: 'Cruises', icon: 'GlobeAltIcon', color: 'text-cyan-400' },
  { id: 'transfer', label: 'Transfers', icon: 'TruckIcon', color: 'text-orange-400' },
  { id: 'bus', label: 'Buses', icon: 'TruckIcon', color: 'text-pink-400' },
  { id: 'insurance', label: 'Insurance', icon: 'ShieldCheckIcon', color: 'text-red-400' },
];

const PACKAGE_TYPES = ['luxury', 'family', 'honeymoon', 'business', 'adventure', 'cultural', 'wellness'];

const SUPPLIERS = [
  'AIRIQ', 'Amadeus', 'Duffel', 'Sabre', 'Travelport', 'UP17', 'TripJack', 'TBO',
  'RateHawk', 'Viator', 'Bokun', 'G Adventures', 'TTC Group', 'TourRadar', 'Crystal Cruises',
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'INR', 'AED', 'SGD', 'AUD'];

// ─── Main Component ───────────────────────────────────────────────────────────
export default function DynamicPackageBuilderContent() {
  const [step, setStep] = useState<'configure' | 'optimize' | 'pricing' | 'review' | 'book'>('configure');
  const [selectedComponents, setSelectedComponents] = useState<string[]>(['flight', 'hotel']);
  const [packageType, setPackageType] = useState('luxury');
  const [travelers, setTravelers] = useState(2);
  const [budget, setBudget] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [membershipTier, setMembershipTier] = useState('standard');
  const [promoCode, setPromoCode] = useState('');
  const [destination, setDestination] = useState('');
  const [duration, setDuration] = useState(7);
  const [optimization, setOptimization] = useState<OptimizationResult | null>(null);
  const [pricing, setPricing] = useState<PricingSummary | null>(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isPricing, setIsPricing] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingResult, setBookingResult] = useState<Record<string, unknown> | null>(null);
  const [activeModal, setActiveModal] = useState<'itinerary' | 'upgrades' | 'alternatives' | 'crosssell' | null>(null);
  const [savedPackages, setSavedPackages] = useState<Array<{ id: string; name: string; type: string; total: number }>>([]);
  const [packageName, setPackageName] = useState('');

  const { error: aiError } = useChat('GEMINI', 'gemini/gemini-2.5-flash', false);
  useEffect(() => { if (aiError) toast.error(aiError.message); }, [aiError]);

  const toggleComponent = (type: string) => {
    setSelectedComponents((prev) =>
      prev.includes(type) ? prev.filter((c) => c !== type) : [...prev, type]
    );
  };

  const handleOptimize = useCallback(async () => {
    if (selectedComponents.length === 0) { toast.error('Select at least one component'); return; }
    setIsOptimizing(true);
    try {
      const res = await fetch('/api/dynamic-packages/optimize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          components: selectedComponents.map((t) => ({ type: t, destination })),
          preferences: { destination, duration, travelers },
          package_type: packageType,
          budget: budget ? Number(budget) : undefined,
          travelers,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setOptimization(data.optimization);
      setStep('optimize');
      toast.success('AI optimization complete!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Optimization failed');
    } finally {
      setIsOptimizing(false);
    }
  }, [selectedComponents, destination, duration, travelers, packageType, budget]);

  const handleCalculatePricing = useCallback(async () => {
    if (!optimization) return;
    setIsPricing(true);
    try {
      const res = await fetch('/api/dynamic-packages/pricing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          components: optimization.optimized_components.map((c) => ({
            type: c.type,
            base_price: c.estimated_price_usd,
            supplier: c.supplier,
            quantity: 1,
          })),
          membership_tier: membershipTier,
          corporate_account: false,
          promo_code: promoCode || undefined,
          currency,
          travelers,
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setPricing(data.pricing_summary);
      setStep('pricing');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Pricing failed');
    } finally {
      setIsPricing(false);
    }
  }, [optimization, membershipTier, promoCode, currency, travelers]);

  const handleSaveDraft = async () => {
    try {
      const res = await fetch('/api/dynamic-packages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: packageName || `${packageType} Package – ${destination || 'Custom'}`,
          package_type: packageType,
          components: optimization?.optimized_components || [],
          pricing: pricing ? { pricing_summary: pricing } : {},
          itinerary: { days: optimization?.day_by_day_itinerary || [] },
          metadata: { destination, duration, travelers },
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setSavedPackages((prev) => [...prev, {
        id: data.package.id,
        name: data.package.name,
        type: data.package.package_type,
        total: pricing?.grand_total_usd || 0,
      }]);
      toast.success('Package saved as draft!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Save failed');
    }
  };

  const handleBook = async () => {
    if (!optimization || !pricing) return;
    setIsBooking(true);
    try {
      const res = await fetch('/api/dynamic-packages/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          components: optimization.optimized_components.map((c) => ({
            type: c.type,
            supplier: c.supplier,
            product_name: c.product_name,
            price: c.estimated_price_usd,
            day: c.day,
          })),
          pricing: { grand_total_usd: pricing.grand_total_usd, display_currency: currency },
          payment_method: 'razorpay',
        }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setBookingResult(data);
      setStep('book');
      toast.success(`Package booked! Ref: ${data.unified_reference}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Booking failed');
    } finally {
      setIsBooking(false);
    }
  };

  const steps = ['configure', 'optimize', 'pricing', 'review', 'book'];
  const stepIdx = steps.indexOf(step);

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      {/* Header */}
      <div className="border-b border-border px-6 py-5" style={{ background: 'var(--surface-2)' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl font-bold text-foreground flex items-center gap-2">
              <Icon name="SparklesIcon" size={24} className="text-primary" />
              Dynamic Package Builder
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Build custom travel packages from 15 global suppliers with AI optimization
            </p>
          </div>
          <div className="flex items-center gap-2">
            {savedPackages.length > 0 && (
              <span className="text-xs text-muted-foreground bg-surface-4 px-3 py-1.5 rounded-lg border border-border">
                {savedPackages.length} draft{savedPackages.length > 1 ? 's' : ''} saved
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Step Progress */}
      <div className="border-b border-border px-6 py-3" style={{ background: 'var(--surface-2)' }}>
        <div className="max-w-6xl mx-auto flex items-center gap-2">
          {['Configure', 'AI Optimize', 'Pricing', 'Review', 'Book'].map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-1.5 text-xs font-medium ${i <= stepIdx ? 'text-primary' : 'text-muted-foreground'}`}>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-2xs font-bold ${i < stepIdx ? 'bg-primary text-white' : i === stepIdx ? 'bg-primary/20 text-primary border border-primary' : 'bg-surface-4 text-muted-foreground border border-border'}`}>
                  {i < stepIdx ? '✓' : i + 1}
                </div>
                <span className="hidden sm:block">{s}</span>
              </div>
              {i < 4 && <div className={`flex-1 h-px ${i < stepIdx ? 'bg-primary/40' : 'bg-border'}`} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* STEP 1: Configure */}
        {step === 'configure' && (
          <div className="space-y-6">
            {/* Package Type */}
            <div className="card p-6">
              <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="TagIcon" size={18} className="text-primary" />
                Package Type
              </h2>
              <div className="flex flex-wrap gap-2">
                {PACKAGE_TYPES.map((t) => (
                  <button
                    key={t}
                    onClick={() => setPackageType(t)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all border ${packageType === t ? 'bg-primary/20 text-primary border-primary/50' : 'border-border text-muted-foreground hover:text-foreground hover:border-border/80'}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Trip Details */}
            <div className="card p-6">
              <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="MapPinIcon" size={18} className="text-primary" />
                Trip Details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs text-muted-foreground mb-1.5">Destination</label>
                  <input
                    type="text"
                    value={destination}
                    onChange={(e) => setDestination(e.target.value)}
                    placeholder="e.g. Maldives, Paris"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-surface-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1.5">Duration (nights)</label>
                  <input
                    type="number"
                    value={duration}
                    onChange={(e) => setDuration(Number(e.target.value))}
                    min={1}
                    max={30}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-surface-4 text-sm text-foreground focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1.5">Travelers</label>
                  <input
                    type="number"
                    value={travelers}
                    onChange={(e) => setTravelers(Number(e.target.value))}
                    min={1}
                    max={50}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-surface-4 text-sm text-foreground focus:outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1.5">Budget (USD, optional)</label>
                  <input
                    type="number"
                    value={budget}
                    onChange={(e) => setBudget(e.target.value)}
                    placeholder="e.g. 10000"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-surface-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                  />
                </div>
              </div>
            </div>

            {/* Components */}
            <div className="card p-6">
              <h2 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="PuzzlePieceIcon" size={18} className="text-primary" />
                Package Components
                <span className="text-xs text-muted-foreground ml-1">({selectedComponents.length} selected)</span>
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {COMPONENT_TYPES.map((c) => {
                  const active = selectedComponents.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => toggleComponent(c.id)}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all ${active ? 'border-primary/50 bg-primary/10' : 'border-border bg-surface-4 hover:border-border/80'}`}
                    >
                      <Icon name={c.icon as Parameters<typeof Icon>[0]['name']} size={22} className={active ? 'text-primary' : c.color} />
                      <span className={`text-xs font-medium ${active ? 'text-primary' : 'text-muted-foreground'}`}>{c.label}</span>
                      {active && <div className="w-1.5 h-1.5 rounded-full bg-primary" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Suppliers */}
            <div className="card p-6">
              <h2 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="BuildingStorefrontIcon" size={18} className="text-primary" />
                Connected Suppliers
                <span className="text-xs text-green-400 ml-2">● {SUPPLIERS.length} Active</span>
              </h2>
              <div className="flex flex-wrap gap-2">
                {SUPPLIERS.map((s) => (
                  <span key={s} className="px-2.5 py-1 rounded-md text-xs font-medium bg-surface-4 text-muted-foreground border border-border">
                    {s}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={handleOptimize}
                disabled={isOptimizing || selectedComponents.length === 0}
                className="btn-primary flex items-center gap-2 px-6 py-3"
              >
                {isOptimizing ? (
                  <><Icon name="ArrowPathIcon" size={18} className="animate-spin" />Optimizing with AI...</>
                ) : (
                  <><Icon name="SparklesIcon" size={18} />Optimize with AI</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: AI Optimization Results */}
        {step === 'optimize' && optimization && (
          <div className="space-y-6">
            {/* Summary Banner */}
            <div className="card p-6 border-primary/30 bg-primary/5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-semibold text-foreground text-lg flex items-center gap-2">
                    <Icon name="SparklesIcon" size={20} className="text-primary" />
                    AI Optimization Complete
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">{optimization.optimization_summary}</p>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-2xl font-bold text-primary">${optimization.total_estimated_cost?.toLocaleString()}</div>
                  <div className="text-xs text-green-400">Save ${optimization.savings_vs_retail?.toLocaleString()} vs retail</div>
                  <div className="text-xs text-muted-foreground mt-0.5">AI Score: {optimization.ai_confidence_score}%</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {optimization.package_highlights?.map((h, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">{h}</span>
                ))}
              </div>
            </div>

            {/* Optimized Components */}
            <div className="card p-6">
              <h3 className="font-semibold text-foreground mb-4">Optimized Components ({optimization.optimized_components?.length})</h3>
              <div className="space-y-3">
                {optimization.optimized_components?.map((comp, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-surface-4 border border-border">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon name={COMPONENT_TYPES.find((c) => c.id === comp.type)?.icon as Parameters<typeof Icon>[0]['name'] || 'StarIcon'} size={16} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate">{comp.product_name}</span>
                        {comp.recommended && <span className="text-2xs px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">AI Pick</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">{comp.supplier} · Day {comp.day} · {comp.duration}</div>
                      {comp.reason && <div className="text-xs text-primary/70 mt-0.5">{comp.reason}</div>}
                    </div>
                    <div className="text-sm font-semibold text-foreground shrink-0">${comp.estimated_price_usd?.toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Day-by-Day Itinerary', icon: 'CalendarDaysIcon', modal: 'itinerary' as const },
                { label: 'Upgrade Options', icon: 'ArrowUpCircleIcon', modal: 'upgrades' as const },
                { label: 'Alternatives', icon: 'ArrowsRightLeftIcon', modal: 'alternatives' as const },
                { label: 'Cross-Sell', icon: 'PlusCircleIcon', modal: 'crosssell' as const },
              ].map((a) => (
                <button
                  key={a.modal}
                  onClick={() => setActiveModal(a.modal)}
                  className="card p-4 text-center hover:border-primary/40 transition-colors"
                >
                  <Icon name={a.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-primary mx-auto mb-2" />
                  <span className="text-xs font-medium text-muted-foreground">{a.label}</span>
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <button onClick={() => setStep('configure')} className="btn-secondary flex items-center gap-2">
                <Icon name="ArrowLeftIcon" size={16} />Back
              </button>
              <div className="flex gap-3">
                <button onClick={handleSaveDraft} className="btn-secondary flex items-center gap-2">
                  <Icon name="BookmarkIcon" size={16} />Save Draft
                </button>
                <button
                  onClick={handleCalculatePricing}
                  disabled={isPricing}
                  className="btn-primary flex items-center gap-2 px-6"
                >
                  {isPricing ? <><Icon name="ArrowPathIcon" size={16} className="animate-spin" />Calculating...</> : <><Icon name="CurrencyDollarIcon" size={16} />Calculate Pricing</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3: Pricing */}
        {step === 'pricing' && pricing && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pricing Breakdown */}
              <div className="card p-6">
                <h2 className="font-semibold text-foreground mb-5 flex items-center gap-2">
                  <Icon name="CurrencyDollarIcon" size={18} className="text-primary" />
                  Pricing Breakdown
                </h2>
                <div className="space-y-3">
                  {[
                    { label: 'Subtotal', value: pricing.subtotal },
                    { label: `Discount (${pricing.discount_type}, ${pricing.discount_applied}%)`, value: -pricing.discount_amount, color: 'text-green-400' },
                    { label: 'After Discount', value: pricing.after_discount },
                    { label: 'Taxes (5%)', value: pricing.taxes },
                    { label: 'Service Fee (2%)', value: pricing.service_fee },
                  ].map((row) => (
                    <div key={row.label} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{row.label}</span>
                      <span className={row.color || 'text-foreground'}>${Math.abs(row.value).toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="border-t border-border pt-3 flex justify-between font-bold text-lg">
                    <span className="text-foreground">Total ({currency})</span>
                    <span className="text-primary">{currency} {pricing.grand_total_display?.toLocaleString()}</span>
                  </div>
                  <div className="text-xs text-muted-foreground text-right">
                    Per person: ${(pricing.grand_total_usd / travelers).toFixed(0)}
                  </div>
                </div>
              </div>

              {/* Pricing Options */}
              <div className="card p-6 space-y-4">
                <h2 className="font-semibold text-foreground flex items-center gap-2">
                  <Icon name="TagIcon" size={18} className="text-primary" />
                  Pricing Options
                </h2>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1.5">Membership Tier</label>
                  <select
                    value={membershipTier}
                    onChange={(e) => setMembershipTier(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-surface-4 text-sm text-foreground focus:outline-none focus:border-primary"
                  >
                    {['standard', 'silver', 'gold', 'platinum'].map((t) => (
                      <option key={t} value={t} className="capitalize">{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1.5">Promo Code</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="e.g. LUXE10"
                      className="flex-1 px-3 py-2 rounded-lg border border-border bg-surface-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
                    />
                    <button onClick={handleCalculatePricing} className="btn-secondary text-xs px-3">Apply</button>
                  </div>
                  <p className="text-2xs text-muted-foreground mt-1">Try: LUXE10, HONEY15, CORP20, SUMMER25</p>
                </div>
                <div>
                  <label className="block text-xs text-muted-foreground mb-1.5">Display Currency</label>
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-surface-4 text-sm text-foreground focus:outline-none focus:border-primary"
                  >
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="pt-2 border-t border-border">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Platform Commission</span>
                    <span>${pricing.total_commission?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-xs text-green-400 mt-1">
                    <span>Estimated Profit</span>
                    <span>${pricing.estimated_profit?.toLocaleString()} ({pricing.profit_margin}%)</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button onClick={() => setStep('optimize')} className="btn-secondary flex items-center gap-2">
                <Icon name="ArrowLeftIcon" size={16} />Back
              </button>
              <div className="flex gap-3">
                <button onClick={handleSaveDraft} className="btn-secondary flex items-center gap-2">
                  <Icon name="BookmarkIcon" size={16} />Save Draft
                </button>
                <button onClick={() => setStep('review')} className="btn-primary flex items-center gap-2 px-6">
                  Review Package <Icon name="ArrowRightIcon" size={16} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 4: Review */}
        {step === 'review' && optimization && pricing && (
          <div className="space-y-6">
            <div className="card p-6">
              <h2 className="font-semibold text-foreground text-lg mb-2">Package Review</h2>
              <p className="text-sm text-muted-foreground mb-6">Review your complete package before booking</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Package Type', value: packageType.charAt(0).toUpperCase() + packageType.slice(1) },
                  { label: 'Destination', value: destination || 'Custom' },
                  { label: 'Travelers', value: travelers.toString() },
                  { label: 'Duration', value: `${duration} nights` },
                ].map((s) => (
                  <div key={s.label} className="bg-surface-4 rounded-lg p-3 border border-border">
                    <div className="text-xs text-muted-foreground">{s.label}</div>
                    <div className="text-sm font-semibold text-foreground mt-0.5">{s.value}</div>
                  </div>
                ))}
              </div>
              <div className="space-y-2 mb-6">
                {optimization.optimized_components?.map((c, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="text-xs px-2 py-0.5 rounded bg-surface-4 text-muted-foreground capitalize">{c.type}</span>
                      <span className="text-sm text-foreground">{c.product_name}</span>
                      <span className="text-xs text-muted-foreground">{c.supplier}</span>
                    </div>
                    <span className="text-sm font-medium text-foreground">${c.estimated_price_usd?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center p-4 rounded-xl bg-primary/5 border border-primary/20">
                <div>
                  <div className="text-sm text-muted-foreground">Total Package Price</div>
                  <div className="text-2xl font-bold text-primary">{currency} {pricing.grand_total_display?.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">${(pricing.grand_total_usd / travelers).toFixed(0)} per person</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Payment via</div>
                  <div className="text-sm font-semibold text-foreground">Razorpay</div>
                  <div className="text-xs text-green-400 mt-0.5">Secure checkout</div>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1.5">Package Name (optional)</label>
              <input
                type="text"
                value={packageName}
                onChange={(e) => setPackageName(e.target.value)}
                placeholder={`${packageType} Package – ${destination || 'Custom'}`}
                className="w-full px-3 py-2 rounded-lg border border-border bg-surface-4 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
              />
            </div>
            <div className="flex items-center justify-between">
              <button onClick={() => setStep('pricing')} className="btn-secondary flex items-center gap-2">
                <Icon name="ArrowLeftIcon" size={16} />Back
              </button>
              <button
                onClick={handleBook}
                disabled={isBooking}
                className="btn-primary flex items-center gap-2 px-8 py-3 text-base"
              >
                {isBooking ? <><Icon name="ArrowPathIcon" size={18} className="animate-spin" />Booking...</> : <><Icon name="CheckCircleIcon" size={18} />Book Package</>}
              </button>
            </div>
          </div>
        )}

        {/* STEP 5: Booking Confirmation */}
        {step === 'book' && bookingResult && (
          <div className="card p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircleIcon" size={32} className="text-green-400" />
            </div>
            <h2 className="font-serif text-2xl font-bold text-foreground mb-2">Package Booked!</h2>
            <p className="text-muted-foreground mb-6">Your travel package has been successfully booked across all suppliers.</p>
            <div className="inline-block bg-surface-4 border border-border rounded-xl px-6 py-4 mb-6">
              <div className="text-xs text-muted-foreground mb-1">Unified Booking Reference</div>
              <div className="text-xl font-mono font-bold text-primary">{bookingResult.unified_reference as string}</div>
            </div>
            <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto mb-8">
              {[
                { label: 'Confirmed', value: bookingResult.confirmed_components as number, color: 'text-green-400' },
                { label: 'Pending', value: 0, color: 'text-amber-400' },
                { label: 'Failed', value: bookingResult.failed_components as number, color: 'text-red-400' },
              ].map((s) => (
                <div key={s.label} className="bg-surface-4 rounded-lg p-3 border border-border">
                  <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                  <div className="text-xs text-muted-foreground">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-3">
              <button onClick={() => { setStep('configure'); setOptimization(null); setPricing(null); setBookingResult(null); }} className="btn-secondary">
                Build Another Package
              </button>
              <a href="/my-bookings" className="btn-primary flex items-center gap-2">
                <Icon name="BookmarkIcon" size={16} />View My Bookings
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <LuxuryModal isOpen={activeModal === 'itinerary'} onClose={() => setActiveModal(null)} title="Day-by-Day Itinerary" size="lg">
        <div className="space-y-4 p-1">
          {optimization?.day_by_day_itinerary?.map((day, i) => (
            <div key={i} className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0 text-sm font-bold text-primary">
                {day.day}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-foreground text-sm">{day.title}</div>
                <div className="text-xs text-muted-foreground mt-1">{day.accommodation}</div>
                <ul className="mt-2 space-y-1">
                  {day.activities?.map((a, j) => (
                    <li key={j} className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-primary/60 shrink-0" />{a}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </LuxuryModal>

      <LuxuryModal isOpen={activeModal === 'upgrades'} onClose={() => setActiveModal(null)} title="Upgrade Options">
        <div className="space-y-3 p-1">
          {optimization?.upgrades_suggested?.map((u, i) => (
            <div key={i} className="p-3 rounded-lg bg-surface-4 border border-border">
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-sm font-medium text-foreground">{u.upgrade}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{u.component} · {u.benefit}</div>
                </div>
                <span className="text-sm font-semibold text-amber-400">+${u.additional_cost?.toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      </LuxuryModal>

      <LuxuryModal isOpen={activeModal === 'alternatives'} onClose={() => setActiveModal(null)} title="Alternative Options">
        <div className="space-y-3 p-1">
          {optimization?.alternatives?.map((a, i) => (
            <div key={i} className="p-3 rounded-lg bg-surface-4 border border-border">
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-sm font-medium text-foreground">{a.alternative}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{a.component}</div>
                </div>
                <span className={`text-sm font-semibold ${a.price_difference < 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {a.price_difference < 0 ? '-' : '+'}${Math.abs(a.price_difference)?.toLocaleString()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </LuxuryModal>

      <LuxuryModal isOpen={activeModal === 'crosssell'} onClose={() => setActiveModal(null)} title="Recommended Add-ons">
        <div className="space-y-3 p-1">
          {optimization?.cross_sell?.map((cs, i) => (
            <div key={i} className="p-3 rounded-lg bg-surface-4 border border-border">
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-sm font-medium text-foreground">{cs.service}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{cs.supplier} · {cs.reason}</div>
                </div>
                <span className="text-sm font-semibold text-primary">${cs.price?.toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      </LuxuryModal>
    </div>
  );
}
